import {
  Fragment,
  HTMLProps,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { DateTime } from "luxon";
import { useForm } from "react-hook-form";
import { DateRangeType } from "react-tailwindcss-datepicker";

import { useQueryClient } from "@tanstack/react-query";
import { createColumnHelper, RowModel } from "@tanstack/react-table";
import {
  AdjustmentsHorizontalIcon,
  ArrowPathIcon,
  ArrowsRightLeftIcon,
  ArrowTopRightOnSquareIcon,
  CheckIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronUpDownIcon,
  ChevronUpIcon,
  CubeIcon,
  HashtagIcon,
  MagnifyingGlassIcon,
  PhotoIcon,
  ShoppingBagIcon,
  TagIcon,
  TruckIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
} from "@headlessui/react";

import PartImage from "./PartImage";
import Modal from "../../layouts/Modal";
import PopOutBox from "../shared/PopOutBox";
import CollapsibleSection from "../shared/CollapsibleSection";
import useBaskets from "../../data/useBaskets";
import classNames from "../../utils/classNames";
import ClickToCopy from "../shared/ClickToCopy";
import { PageMode } from "../../models/PagedTable";
import openInNewTab from "../../utils/openInNewTab";
import PagedTable from "../shared/Tables/PagedTable";
import SlimSelectCol from "../shared/Tables/SlimSelectCol";
import { MakeCombobox } from "../parts/MakeCombobox";
import { IPart, IPartCategory, PartStatus } from "../../models/Part";
import { IVehicle } from "../../models/Vehicle";
import ContactFromId from "../contacts/ContactFromId";
import SelectInput from "../shared/Inputs/SelectInput";
import { TextInput } from "../shared/Inputs/TextInput";
import { ModelCombobox } from "../parts/ModelCombobox";
import { TrimCombobox } from "../parts/TrimCombobox";
import { YearCombobox } from "../parts/YearCombobox";
import ConfirmModal from "../shared/Confirm/ConfirmModal";
import usePartMutations from "../../data/usePartMutations";
import { FormatType, ListingStatusEnum } from "../../models/EbayOffer";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import useNotification from "../notifications/useNotifications";
import usePartDetails from "../../data/usePartDetails";
import LoadingWheel from "../shared/LoadingWheel";
import { useLabelGenerator } from "../../hooks/useLabelGenerator";
import useVehiclesMutations from "../../data/useVehiclesMutations";
import PartCategoryCombo from "../leads/LeadParts/PartCategoryCombo";
import ChangeCategoryModal from "./stockFunctions/ChangeCatgeoryModal";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import UnlistedNpmUpdate from "../ebayListings/unlistedTable/UnlistedMpnUpdate";
import UnlistedOfferPrice from "../ebayListings/unlistedTable/UnlistedOfferPrice";
import StockMpnCell from "./StockMpnCell";
import VehicleCompareModal from "../shared/VehicleCompareModal";
import PartGradeBadge from "../shared/PartGradeBadge";
import PartNotesIndicator from "../shared/PartNotesIndicator";
import SendToQcModal from "./SendToQcModal";
import WarehouseLocationFromId from "../warehouseLocations/WarehouseLocationFromId";
import WarehouseLocationSearch from "../warehouseLocations/WarehouseLocationSearch";
import useWarehouseLocations from "../../data/useWarehouseLocations";
import ComboBoxInput from "../shared/Inputs/ComboBoxInput";
import { IWarehouseLocation } from "../../models/Location";
import DateRangeFilter from "../shared/DateRangeFilter";
import useCategories from "../../data/useCategories";
import Tooltip from "../shared/Tooltip";
import useCategoriesPartial from "../../data/useCategoriesPartial";
import BulkAddToWorkOrderModal from "../workOrders/BulkAddToWorkOrderModal";
import { SearchPaletteItem } from "../shared/Inputs/SearchPaletteTypes";

const colHelper = createColumnHelper<IPart>();


type LocationSearchMode = "equals" | "beginsWith";

type TriState = "any" | "with" | "without";

// Static-options combobox styled to match the Make / Model / Trim / Year
// comboboxes. Allows free text entry too so unusual vehicle attributes
// (e.g. obscure engine codes) can still be searched.
function FilterStaticCombobox({
  label,
  value,
  onChange,
  options,
  disabled = false,
  placeholder,
}: {
  label: string;
  value: string | undefined;
  onChange: (next: string) => void;
  options: string[];
  disabled?: boolean;
  placeholder?: string;
}) {
  const [searchTerm, setSearchTerm] = useState(value ?? "");

  useEffect(() => {
    setSearchTerm(value ?? "");
  }, [value]);

  const filtered = useMemo(() => {
    if (!searchTerm) return options;
    const needle = searchTerm.toLowerCase();
    return options.filter((opt) => opt.toLowerCase().includes(needle));
  }, [options, searchTerm]);

  return (
    <div>
      <label>
        <span className="text-sm font-medium text-black">{label}</span>
      </label>
      <Combobox
        as="div"
        value={value ?? ""}
        onChange={(next: string) => {
          onChange(next ?? "");
          setSearchTerm(next ?? "");
        }}
      >
        <div className="relative mt-1">
          <ComboboxInput
            className="w-full py-2 pl-3 pr-10 text-sm leading-5 text-gray-900 border-1 border-gray-400 rounded-md disabled:bg-gray-100"
            onChange={(event) => {
              setSearchTerm(event.target.value);
              onChange(event.target.value);
            }}
            displayValue={(v: string) => v || searchTerm}
            placeholder={placeholder}
            disabled={disabled}
            autoComplete="off"
          />
          <ComboboxButton className="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none">
            <ChevronUpDownIcon className="size-5 text-gray-400" aria-hidden="true" />
          </ComboboxButton>

          <ComboboxOptions className="absolute z-30 mt-1 max-h-60 w-full overflow-auto bg-white py-1 text-base rounded-md ring-1 ring-gray-400 ring-opacity-5 focus:outline-none sm:text-sm">
            {filtered.length > 0 ? (
              filtered.slice(0, 100).map((opt) => (
                <ComboboxOption
                  key={opt}
                  value={opt}
                  className={({ active }) =>
                    `relative cursor-default select-none py-2 pl-10 pr-4 ${active ? "bg-primary-600 text-white" : "text-gray-900"}`
                  }
                >
                  {({ selected, active }) => (
                    <>
                      <span className={`block truncate ${selected ? "font-medium" : "font-normal"}`}>
                        {opt}
                      </span>
                      {selected ? (
                        <span
                          className={`absolute inset-y-0 left-0 flex items-center pl-3 ${active ? "text-white" : "text-primary-600"}`}
                        >
                          <CheckIcon className="h-5 w-5" aria-hidden="true" />
                        </span>
                      ) : null}
                    </>
                  )}
                </ComboboxOption>
              ))
            ) : (
              <div className="relative cursor-default select-none py-2 px-4 text-gray-700">
                Press enter to use "{searchTerm}".
              </div>
            )}
          </ComboboxOptions>
        </div>
      </Combobox>
    </div>
  );
}

// Map the user-facing "Any | With | Without" pills onto the boolean
// `partsWith*` / `partsWithout*` flags the stock search body uses.
// Backend support for these is tracked in docs/pending-endpoints.md.
function buildTriStateFlags(state: {
  hasWorkOrder: TriState | undefined;
  hasImages: TriState | undefined;
  hasMPN: TriState | undefined;
  hasLocation: TriState | undefined;
}): Record<string, true | undefined> {
  const map = (
    v: TriState | undefined,
    withKey: string,
    withoutKey: string
  ): Record<string, true | undefined> => {
    if (v === "with") return { [withKey]: true };
    if (v === "without") return { [withoutKey]: true };
    return {};
  };

  return {
    ...map(state.hasWorkOrder, "partsWithWorkOrder", "partsWithoutWorkOrder"),
    ...map(state.hasImages, "partsWithImages", "partsWithoutImages"),
    ...map(state.hasMPN, "partsWithMPN", "partsWithoutMPN"),
    ...map(state.hasLocation, "partsWithLocation", "partsWithoutLocations"),
  };
}

// Generic segmented-button pill used in the filters strip.
function FilterSegmented<T extends string | number>({
  label,
  value,
  onChange,
  segments,
  disabled = false,
}: {
  label: string;
  value: T | undefined;
  onChange: (next: T) => void;
  segments: { value: T; label: string }[];
  disabled?: boolean;
}) {
  return (
    <div className="flex flex-col">
      <span className="text-sm font-medium text-black">{label}</span>
      <div
        role="group"
        aria-label={label}
        className={classNames(
          "mt-1 inline-flex rounded-md border border-gray-300 bg-white p-0.5",
          disabled ? "opacity-50" : ""
        )}
      >
        {segments.map((seg) => {
          const active = value === seg.value;
          return (
            <button
              key={String(seg.value)}
              type="button"
              disabled={disabled}
              onClick={() => onChange(seg.value)}
              className={classNames(
                "px-2.5 py-1 text-xs font-medium rounded-[5px] transition-colors whitespace-nowrap",
                active
                  ? "bg-primary-600 text-white shadow-sm"
                  : "text-gray-600 hover:bg-gray-100",
                disabled ? "cursor-not-allowed" : "cursor-pointer"
              )}
              aria-pressed={active}
            >
              {seg.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// Three-segment Any / With / Without pill used in the filters strip.
function FilterTriToggle({
  label,
  value,
  onChange,
  disabled = false,
}: {
  label: string;
  value: TriState | undefined;
  onChange: (next: TriState) => void;
  disabled?: boolean;
}) {
  return (
    <FilterSegmented<TriState>
      label={label}
      value={value ?? "any"}
      onChange={onChange}
      disabled={disabled}
      segments={[
        { value: "any", label: "Any" },
        { value: "with", label: "With" },
        { value: "without", label: "Without" },
      ]}
    />
  );
}

const BODY_STYLE_OPTIONS = [
  "Hatchback",
  "Saloon",
  "Estate",
  "SUV",
  "Coupe",
  "Convertible",
  "MPV",
  "Van",
  "Pickup",
  "Crossover",
];
const DOORS_OPTIONS = ["2", "3", "4", "5"];
const FUEL_OPTIONS = ["Petrol", "Diesel", "Electric", "Hybrid", "Plug-in Hybrid", "LPG"];
const TRANSMISSION_OPTIONS = ["Manual", "Automatic", "Semi-Automatic", "CVT", "DSG"];
const ENGINE_SIZE_OPTIONS = [
  "1.0", "1.2", "1.4", "1.5", "1.6", "1.8",
  "2.0", "2.2", "2.5", "3.0", "3.5", "4.0", "5.0",
];

interface IFormFields {
  make: string;
  model: string;
  trim: string;
  year: string;
  bodyStyle: string;
  numberOfDoors: string;
  fuelType: string;
  transmissionType: string;
  engineSize: string;
  engineCode: string;
  yearFrom: string;
  yearTo: string;
  partNumber: string;
  partCategory: IPartCategory | undefined;
  vrm: string;
  tagNumber: string;
  smartSearch: string;
  dvlaYearOfManufacture: string;
  visibilityDate: string;
  terminateDate: string;
  listingStatus: ListingStatusEnum;
  locationSearchMode: LocationSearchMode;
  warehouseLocation: IWarehouseLocation | undefined;
  warehouseLocationId: string | undefined;
  warehouseLocationBeginsWith: string;
  dateFieldType: string | undefined;
  hasWorkOrder: TriState;
  hasImages: TriState;
  hasMPN: TriState;
  hasLocation: TriState;
  partStatus: number;
};

const defaultFormValues: IFormFields = {
  make: "",
  model: "",
  trim: "",
  year: "",
  bodyStyle: "",
  numberOfDoors: "",
  fuelType: "",
  transmissionType: "",
  engineSize: "",
  engineCode: "",
  yearFrom: "",
  yearTo: "",
  partNumber: "",
  partCategory: undefined,
  vrm: "",
  smartSearch: "",
  dvlaYearOfManufacture: "",
  visibilityDate: "",
  terminateDate: "",
  tagNumber: "",
  listingStatus: ListingStatusEnum["All"],
  locationSearchMode: "equals",
  warehouseLocation: undefined,
  warehouseLocationId: undefined,
  warehouseLocationBeginsWith: "",
  dateFieldType: "Created",
  hasWorkOrder: "any",
  hasImages: "any",
  hasMPN: "any",
  hasLocation: "any",
  partStatus: -1,
};

interface IStockTableProps {
  showActions?: boolean;
  showStatus?: boolean;
  tableKey?: string;
  tableUrl?: string;

  pageMode?: PageMode;
  externalFilters?: Record<string, any>;
  filterBanner?: React.ReactNode;
  editableMpn?: boolean;
  // Adds a dedicated column showing the `qcReason` captured when a part was
  // sent to QC / deleted / scrapped. Only enabled on the Stock QC page today.
  showQcReason?: boolean;
}

// SlimSelectCol has been extracted to src/components/shared/Tables/SlimSelectCol.tsx
// so the Awaiting Inventory table can share the same row-selection styling.

// PartGradeBadge (with GRADE_STYLES/GRADE_DEFINITIONS) extracted to
// src/components/shared/PartGradeBadge.tsx for reuse on the basket page.
// PartNotesIndicator extracted to src/components/shared/PartNotesIndicator.tsx
// for the same reason.

function CopyText({
  text,
  className = "",
  title,
}: {
  text: string | number | null | undefined;
  className?: string;
  title?: string;
}) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const t = setTimeout(() => setCopied(false), 1200);
    return () => clearTimeout(t);
  }, [copied]);

  if (text === null || text === undefined || text === "") return null;

  const value = String(text);

  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard
      .writeText(value)
      .then(() => setCopied(true))
      .catch((err) => console.error("Failed to copy:", err));
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      title={title ?? (copied ? "Copied!" : "Click to copy")}
      aria-live="polite"
      className={classNames(
        "inline-flex items-center gap-1 rounded px-1 -mx-1 text-left transition-all duration-150 cursor-pointer focus:outline-none focus:ring-1 focus:ring-primary-400",
        copied
          ? "bg-emerald-100 text-emerald-700 ring-1 ring-emerald-200"
          : "hover:bg-slate-100 hover:text-primary-600",
        className
      )}
    >
      {copied ? (
        <>
          <CheckIcon className="h-3.5 w-3.5 flex-none" aria-hidden="true" />
          <span className="font-medium">Copied!</span>
        </>
      ) : (
        <span className="truncate">{value}</span>
      )}
    </button>
  );
}

// VehicleCompareModal extracted to src/components/shared/VehicleCompareModal.tsx
// for reuse on the basket page.

type VehicleSearchHighlights = Partial<{
  vrm: string;
  make: string;
  model: string;
  trim: string;
  year: string | number;
  yearFrom: string | number;
  yearTo: string | number;
  bodyStyle: string;
  numberOfDoors: string | number;
  fuelType: string;
  transmissionType: string;
  engineCode: string;
  engineSize: string | number;
}>;

const VehicleSearchHighlightsContext = createContext<VehicleSearchHighlights>({});

const useVehicleSearchHighlights = () => useContext(VehicleSearchHighlightsContext);

const StockSearchVrmContext = createContext<string>("");
const useStockSearchVrm = () => useContext(StockSearchVrmContext);

const norm = (v: unknown) =>
  v === null || v === undefined ? "" : String(v).trim().toLowerCase();

const includesMatch = (haystack: unknown, needle: unknown) => {
  const h = norm(haystack);
  const n = norm(needle);
  return n.length > 0 && h.length > 0 && h.includes(n);
};

function DonorVehicleCell({ part, twoColumn = false }: { part: IPart; twoColumn?: boolean }) {
  const [compareOpen, setCompareOpen] = useState(false);
  const highlights = useVehicleSearchHighlights();
  const searchVrm = useStockSearchVrm();
  const vehicle = part.vehicle;
  if (!vehicle) {
    return <span className="text-xs text-slate-400">—</span>;
  }

  const mileage =
    typeof vehicle.odometerInMiles === "number"
      ? `${vehicle.odometerInMiles.toLocaleString("en-GB")} mi`
      : null;

  const bodyParts = [
    vehicle.numberOfDoors ? `${vehicle.numberOfDoors}dr` : null,
    vehicle.bodyStyle || vehicle.bodyStyleDescription || null,
  ].filter(Boolean);
  const body = bodyParts.length > 0 ? bodyParts.join(" ") : null;

  const fuel = vehicle.combinedFuelType || vehicle.fuelType || vehicle.fuelDescription || null;
  const transmission = vehicle.combinedTransmission || vehicle.dvlaTransmission || vehicle.transmission || null;
  const bhp = typeof vehicle.maximumPowerBHP === "number" ? `${vehicle.maximumPowerBHP} bhp` : null;

  const yearNum = vehicle.dvlaYearOfManufacture ?? null;
  const yearMatched = (() => {
    if (yearNum === null) return false;
    if (highlights.year && includesMatch(yearNum, highlights.year)) return true;
    const yf = highlights.yearFrom ? Number(highlights.yearFrom) : null;
    const yt = highlights.yearTo ? Number(highlights.yearTo) : null;
    if (yf !== null && !Number.isNaN(yf) && yearNum < yf) return false;
    if (yt !== null && !Number.isNaN(yt) && yearNum > yt) return false;
    return (yf !== null && !Number.isNaN(yf)) || (yt !== null && !Number.isNaN(yt));
  })();

  const fields: {
    label: string;
    value: React.ReactNode | null;
    matched?: boolean;
  }[] = [
    {
      label: "Reg",
      value: vehicle.vrm ? <CopyText text={vehicle.vrm} /> : null,
      matched: includesMatch(vehicle.vrm, highlights.vrm),
    },
    {
      label: "VIN",
      value: (() => {
        const vin = vehicle.combinedVIN || vehicle.vinOriginalDVLA || null;
        return vin ? <CopyText text={vin} /> : null;
      })(),
    },
    { label: "Year", value: yearNum, matched: yearMatched },
    {
      label: "Make",
      value: vehicle.combinedMake || null,
      matched:
        includesMatch(vehicle.combinedMake, highlights.make) ||
        includesMatch(vehicle.dvlaMake, highlights.make),
    },
    {
      label: "Model",
      value: vehicle.combinedModel || null,
      matched:
        includesMatch(vehicle.combinedModel, highlights.model) ||
        includesMatch(vehicle.dvlaModel, highlights.model),
    },
    {
      label: "Trim",
      value: vehicle.modelVariantDescription || null,
      matched: includesMatch(vehicle.modelVariantDescription, highlights.trim),
    },
    { label: "Mileage", value: mileage },
    {
      label: "Body",
      value: body,
      matched:
        includesMatch(vehicle.bodyStyle, highlights.bodyStyle) ||
        includesMatch(vehicle.bodyStyleDescription, highlights.bodyStyle) ||
        (highlights.numberOfDoors
          ? includesMatch(vehicle.numberOfDoors, highlights.numberOfDoors)
          : false),
    },
    {
      label: "Fuel",
      value: fuel,
      matched:
        includesMatch(vehicle.combinedFuelType, highlights.fuelType) ||
        includesMatch(vehicle.fuelType, highlights.fuelType) ||
        includesMatch(vehicle.fuelDescription, highlights.fuelType),
    },
    {
      label: "Engine Code",
      value: vehicle.engineModelCode ? <CopyText text={vehicle.engineModelCode} /> : null,
      matched: includesMatch(vehicle.engineModelCode, highlights.engineCode),
    },
    {
      label: "Transmission",
      value: transmission,
      matched:
        includesMatch(vehicle.combinedTransmission, highlights.transmissionType) ||
        includesMatch(vehicle.dvlaTransmission, highlights.transmissionType) ||
        includesMatch(vehicle.transmission, highlights.transmissionType),
    },
    { label: "BHP", value: bhp },
  ].filter((f) => f.value !== null && f.value !== "");

  const renderFieldList = (list: typeof fields) => (
    <dl className="grid min-w-0 grid-cols-[auto,1fr] gap-x-2 gap-y-0.5 text-xs">
      {list.map((f) => (
        <Fragment key={f.label}>
          <dt className="font-medium text-slate-400">{f.label}</dt>
          <dd className="min-w-0 truncate text-slate-700">{f.value}</dd>
        </Fragment>
      ))}
    </dl>
  );

  let primaryFields = fields;
  let secondaryFields: typeof fields = [];
  if (twoColumn) {
    const mileageIdx = fields.findIndex((f) => f.label === "Mileage");
    const splitAt = mileageIdx === -1 ? Math.ceil(fields.length / 2) : mileageIdx + 1;
    primaryFields = fields.slice(0, splitAt);
    secondaryFields = fields.slice(splitAt);
  }

  return (
    <div className="flex items-start gap-2">
      <div className="flex min-w-0 flex-1 gap-3">
        {primaryFields.length > 0 && renderFieldList(primaryFields)}
        {secondaryFields.length > 0 && renderFieldList(secondaryFields)}
      </div>
      <div className="mt-0.5 flex flex-none flex-col items-center gap-1">
        {vehicle.id && (
          <Tooltip content="View vehicle">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                openInNewTab(`/vehicles/${vehicle.id}`);
              }}
              aria-label="View vehicle"
              className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-primary-600 text-white shadow-sm ring-1 ring-primary-700/20 transition-all hover:bg-primary-700 hover:shadow focus:outline-none focus:ring-2 focus:ring-primary-400 focus:ring-offset-1"
            >
              <TruckIcon className="h-4 w-4" aria-hidden="true" />
            </button>
          </Tooltip>
        )}
        <Tooltip content="Compare vehicle">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setCompareOpen(true);
            }}
            aria-label="Compare vehicle"
            className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-white text-slate-600 shadow-sm ring-1 ring-slate-300 transition-all hover:bg-slate-50 hover:text-primary-600 hover:ring-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:ring-offset-1"
          >
            <ArrowsRightLeftIcon className="h-4 w-4" aria-hidden="true" />
          </button>
        </Tooltip>
      </div>

      <VehicleCompareModal
        open={compareOpen}
        setOpen={setCompareOpen}
        donor={vehicle}
        initialVrm={searchVrm}
      />
    </div>
  );
}

function PartImagesGallery({ part }: { part: IPart }) {
  // Stock list response only includes the main thumbnail per part; fetch full
  // details on expand to get the complete image set.
  const { partDetails, isLoading: detailsLoading } = usePartDetails(part.id);

  const sortedImages = useMemo(() => {
    const fromDetails = Array.isArray(partDetails?.images) ? partDetails!.images : [];
    const fromRow = Array.isArray(part.images) ? part.images : [];
    const source = fromDetails.length > 0 ? fromDetails : fromRow;
    return [...source].sort((a, b) => {
      if (a.isThumbnail && !b.isThumbnail) return -1;
      if (!a.isThumbnail && b.isThumbnail) return 1;
      const ao = a.order ?? Number.MAX_SAFE_INTEGER;
      const bo = b.order ?? Number.MAX_SAFE_INTEGER;
      return ao - bo;
    });
  }, [partDetails?.images, part.images]);

  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const isOpen = lightboxIndex !== null;

  const close = () => setLightboxIndex(null);
  const showPrev = () =>
    setLightboxIndex((i) =>
      i === null ? null : (i - 1 + sortedImages.length) % sortedImages.length
    );
  const showNext = () =>
    setLightboxIndex((i) => (i === null ? null : (i + 1) % sortedImages.length));

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        showPrev();
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        showNext();
      } else if (e.key === "Escape") {
        close();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, sortedImages.length]);

  if (sortedImages.length === 0) {
    if (detailsLoading) {
      return (
        <div className="flex items-center justify-center gap-2 p-6 text-sm text-slate-500">
          <LoadingWheel />
          <span>Loading images…</span>
        </div>
      );
    }
    return (
      <div className="flex items-center justify-center gap-2 p-6 text-sm text-slate-500">
        <PhotoIcon className="h-5 w-5" aria-hidden="true" />
        <span>No images uploaded for this part yet.</span>
      </div>
    );
  }

  const currentImage = lightboxIndex !== null ? sortedImages[lightboxIndex] : null;
  const currentSrc = currentImage?.url ?? currentImage?.thumbnailUrl ?? "";

  return (
    <div className="bg-slate-50 p-3">
      <div className="mb-2 flex items-center justify-between">
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          Images ({sortedImages.length})
        </p>
        <p className="text-xs text-slate-400">Click a thumbnail to enlarge</p>
      </div>
      <div className="flex flex-wrap gap-2">
        {sortedImages.map((img, idx) => {
          const thumb = img.thumbnailUrl ?? img.url;
          return (
            <button
              key={img.id}
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setLightboxIndex(idx);
              }}
              title="Click to enlarge"
              className={classNames(
                "group relative h-24 w-24 overflow-hidden rounded-md border bg-white shadow-sm transition-all",
                "hover:shadow-md focus:outline-none focus:ring-2 focus:ring-primary-400",
                img.isThumbnail
                  ? "border-primary-400 ring-1 ring-primary-200"
                  : "border-slate-200"
              )}
            >
              <img
                src={thumb}
                alt=""
                loading="lazy"
                className="h-full w-full object-cover transition-transform duration-150 group-hover:scale-105"
              />
              {img.isThumbnail && (
                <span className="absolute left-1 top-1 rounded-sm bg-primary-600/90 px-1.5 py-0.5 text-[10px] font-semibold text-white">
                  Main
                </span>
              )}
            </button>
          );
        })}
      </div>

      <Modal
        open={isOpen}
        setOpen={(v) => {
          const next = typeof v === "function" ? v(isOpen) : v;
          if (!next) close();
        }}
        width="max-w-5xl"
      >
        {currentImage && (
          <div
            className="relative flex flex-col items-center gap-3"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex w-full items-center justify-between text-xs text-slate-500">
              <span>
                Image {(lightboxIndex ?? 0) + 1} of {sortedImages.length}
              </span>
              <button
                type="button"
                onClick={close}
                aria-label="Close image viewer"
                className="inline-flex h-8 w-8 items-center justify-center rounded-full text-slate-500 hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus:ring-2 focus:ring-primary-400"
              >
                <XMarkIcon className="h-5 w-5" aria-hidden="true" />
              </button>
            </div>

            <div className="relative flex w-full items-center justify-center">
              {sortedImages.length > 1 && (
                <button
                  type="button"
                  onClick={showPrev}
                  aria-label="Previous image"
                  className="absolute left-0 top-1/2 z-10 -translate-y-1/2 inline-flex h-10 w-10 items-center justify-center rounded-full bg-white/90 text-slate-700 shadow ring-1 ring-slate-200 hover:bg-white focus:outline-none focus:ring-2 focus:ring-primary-400"
                >
                  <ChevronLeftIcon className="h-6 w-6" aria-hidden="true" />
                </button>
              )}

              <img
                src={currentSrc}
                alt=""
                className="max-h-[70vh] w-auto max-w-full rounded-md object-contain"
              />

              {sortedImages.length > 1 && (
                <button
                  type="button"
                  onClick={showNext}
                  aria-label="Next image"
                  className="absolute right-0 top-1/2 z-10 -translate-y-1/2 inline-flex h-10 w-10 items-center justify-center rounded-full bg-white/90 text-slate-700 shadow ring-1 ring-slate-200 hover:bg-white focus:outline-none focus:ring-2 focus:ring-primary-400"
                >
                  <ChevronRightIcon className="h-6 w-6" aria-hidden="true" />
                </button>
              )}
            </div>

            {sortedImages.length > 1 && (
              <div className="flex w-full flex-wrap justify-center gap-2 pt-2">
                {sortedImages.map((img, idx) => {
                  const thumb = img.thumbnailUrl ?? img.url;
                  const isActive = idx === lightboxIndex;
                  return (
                    <button
                      key={img.id}
                      type="button"
                      onClick={() => setLightboxIndex(idx)}
                      aria-label={`View image ${idx + 1}`}
                      className={classNames(
                        "h-14 w-14 overflow-hidden rounded-md border transition-all",
                        isActive
                          ? "border-primary-500 ring-2 ring-primary-300"
                          : "border-slate-200 opacity-70 hover:opacity-100"
                      )}
                    >
                      <img src={thumb} alt="" className="h-full w-full object-cover" />
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}

// Compact icon-trio cell summarising eBay / Order / Part status with tooltips.
function StatusIcons({ part }: { part: IPart }) {
  const offer = part.offers?.[0];

  const ebay = (() => {
    if (part.soldDate) return { label: "Sold", color: "bg-emerald-100 text-emerald-600" };
    if (part.listingProcessing) return { label: "Listing in progress", color: "bg-purple-100 text-purple-600" };
    if (offer?.publishedOn && offer?.listingStarted && new Date(offer.listingStarted) < new Date()) {
      return { label: "Active on eBay", color: "bg-blue-100 text-blue-600" };
    }
    if (offer?.listingEnded && new Date(offer.listingEnded) < new Date()) {
      return { label: "Ended - not sold", color: "bg-rose-100 text-rose-600" };
    }
    if (offer?.publishedOn) return { label: "Published", color: "bg-amber-100 text-amber-600" };
    return { label: "Not published", color: "bg-slate-100 text-slate-400" };
  })();

  const order = (() => {
    if (part.shippedOn) return { label: "Shipped", color: "bg-emerald-100 text-emerald-600" };
    if (part.pickedOn) return { label: "Picked", color: "bg-blue-100 text-blue-600" };
    if (part.soldDate) return { label: "Awaiting pick", color: "bg-amber-100 text-amber-600" };
    return { label: "No order", color: "bg-slate-100 text-slate-400" };
  })();

  const partStatusValue = part.partStatus as PartStatus | null | undefined;
  const partInfo = (() => {
    if (partStatusValue === null || partStatusValue === undefined) {
      return { label: "Status N/A", color: "bg-slate-100 text-slate-400" };
    }
    const label = PartStatus[partStatusValue] ?? "Unknown";
    let color = "bg-slate-100 text-slate-500";
    switch (partStatusValue) {
      case PartStatus.Dismantled:
        color = "bg-emerald-100 text-emerald-600";
        break;
      case PartStatus["Pending Dismantle"]:
        color = "bg-amber-100 text-amber-600";
        break;
      case PartStatus.QC:
        color = "bg-indigo-100 text-indigo-600";
        break;
      case PartStatus.Damaged:
      case PartStatus.Missing:
      case PartStatus.Returned:
      case PartStatus.Deleted:
        color = "bg-rose-100 text-rose-600";
        break;
      case PartStatus.Sold:
        color = "bg-blue-100 text-blue-600";
        break;
      case PartStatus["On The Car"]:
        color = "bg-slate-100 text-slate-500";
        break;
    }
    return { label, color };
  })();

  const Pill = ({
    color,
    label,
    Icon,
  }: {
    color: string;
    label: string;
    Icon: typeof TagIcon;
  }) => (
    <Tooltip content={label}>
      <span
        className={classNames(
          "inline-flex h-7 w-7 items-center justify-center rounded-full",
          color
        )}
      >
        <Icon className="h-4 w-4" aria-hidden="true" />
      </span>
    </Tooltip>
  );

  return (
    <div className="flex items-center gap-1.5">
      <Pill color={ebay.color} label={`eBay: ${ebay.label}`} Icon={TagIcon} />
      <Pill color={order.color} label={`Order: ${order.label}`} Icon={ShoppingBagIcon} />
      <Pill color={partInfo.color} label={`Part: ${partInfo.label}`} Icon={CubeIcon} />
      {part.ebayItemNumber && (
        <Tooltip content="View listing on eBay">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              openInNewTab(`https://www.ebay.co.uk/itm/${part.ebayItemNumber}`);
            }}
            aria-label="View listing on eBay"
            className="inline-flex h-7 w-7 items-center justify-center rounded-full text-slate-500 transition-colors hover:bg-slate-100 hover:text-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-400"
          >
            <ArrowTopRightOnSquareIcon className="h-4 w-4" aria-hidden="true" />
          </button>
        </Tooltip>
      )}
    </div>
  );
}

export default function StockTable({
  showActions = true,
  showStatus = true,
  tableKey = "stock-search",
  tableUrl = "/part/stock",
  pageMode = PageMode.Stock,
  externalFilters,
  filterBanner,
  editableMpn = false,
  showQcReason = false,
}: IStockTableProps) {
  const { addNotification } = useNotification();
  const { addItemsMultiple } = useBaskets("StockTable");
  const { vrmLookup } = useVehiclesMutations();
  const { bulkPatchPart, deletePart, bulkUpdateOfferPrice } = usePartMutations();
  const { warehouseLocations } = useWarehouseLocations();
  const { categories: allCategories } = useCategoriesPartial();
  const { generatePartLabel } = useLabelGenerator();
  const { saveHandlers, deleteHandlers } = useDefaultCRUDHandlers("Part");
  const queryClient = useQueryClient();
  const { register, setValue, getValues, watch, reset, handleSubmit, formState } = useForm<IFormFields>({
    defaultValues: defaultFormValues,
  });
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [toDelete, setToDelete] = useState<RowModel<IPart> | undefined>(undefined);
  const [toUpdate, setToUpdate] = useState<RowModel<IPart> | undefined>(undefined);
  const [statusUpdate, setStatusUpdate] = useState<number>(0);
  const [priceUpdate, setPriceUpdate] = useState<number>(0);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [isSmartMatch, setIsSmartMatch] = useState(false);
  const [isExtended, setIsExtended] = useState(true);
  const [updateStatusOpen, setUpdateStatusOpen] = useState(false);
  const [updatePriceOpen, setUpdatePriceOpen] = useState(false);
  const [updateCategoryOpen, setUpdateCategoryOpen] = useState(false);
  const [sendToQcOpen, setSendToQcOpen] = useState(false);
  const [partsToQc, setPartsToQc] = useState<IPart[]>([]);
  const [additionalBody, setAdditionalBody] = useState<Record<string, any>>({});
  const [selectedCategory, setSelectedCategory] = useState<string | undefined>(undefined);
  const [isSearching, setIsSearching] = useState(false);
  const [isBulkWorkOrderOpen, setIsBulkWorkOrderOpen] = useState(false);
  const [workOrderItems, setWorkOrderItems] = useState<SearchPaletteItem[]>([]);
  const [queryInput, setQueryInput] = useState("");
  const [appliedQuery, setAppliedQuery] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(true);
  const [dates, setDates] = useState<DateRangeType>({
    startDate: null,
    endDate: null
  });
  const [dateFieldType, setDateFieldType] = useState<string>("Created");

  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const vehicleTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const ebayListingSegments = (() => {
    const visibleLabels = ["All", "Published", "Not Published"];
    const all = Object.keys(ListingStatusEnum)
      .filter((key) => isNaN(Number(key)))
      .filter((key) => visibleLabels.includes(key))
      .map((key) => ({
        value: ListingStatusEnum[key as keyof typeof ListingStatusEnum] as number,
        label: key,
      }));
    // Preserve the explicit display order (All first).
    return visibleLabels
      .map((label) => all.find((s) => s.label === label))
      .filter((s): s is { value: number; label: string } => Boolean(s));
  })();
  const ebayAllValue = ListingStatusEnum["All"] as number;

  const partStatusOptions = [
    { id: -1, name: "All" },
    ...Object.keys(PartStatus)
      .filter((key) => isNaN(Number(key)))
      .map((key) => ({
        id: PartStatus[key as keyof typeof PartStatus] as number,
        name: key,
      })),
  ];

  const vehicleSearchHighlights = useMemo<VehicleSearchHighlights>(() => {
    const src: Record<string, any> = {
      ...(additionalBody ?? {}),
      ...(externalFilters ?? {}),
    };
    const pick = (key: string) => {
      const v = src[key];
      return v === null || v === undefined || v === "" ? undefined : v;
    };
    return {
      vrm: pick("vrm"),
      make: pick("make"),
      model: pick("model"),
      trim: pick("trim"),
      year: pick("year") ?? pick("dvlaYearOfManufacture"),
      yearFrom: pick("yearFrom"),
      yearTo: pick("yearTo"),
      bodyStyle: pick("bodyStyle"),
      numberOfDoors: pick("numberOfDoors"),
      fuelType: pick("fuelType"),
      transmissionType: pick("transmissionType"),
      engineCode: pick("engineCode"),
      engineSize: pick("engineSize"),
    };
  }, [additionalBody, externalFilters]);


  const cols = useMemo(
    () => [
      SlimSelectCol(),
      colHelper.display({
        id: "images",
        meta: editableMpn
          ? {
              thClassName: "!px-2 w-40 min-w-[10rem] max-w-[10rem]",
              tdClassName: "!px-2 w-40 min-w-[10rem] max-w-[10rem]",
            }
          : { thClassName: "!px-2 w-16", tdClassName: "!px-2 w-16" },
        cell: (info) => (
          <PartImage
            part={info.row.original}
            width={editableMpn ? "min-w-20 max-w-40" : "min-w-10 max-w-24"}
          />
        ),
      }),
      colHelper.accessor(row => row.category?.name, {
        header: "Part Information",
        id: "categoryId",
        sortingFn: "auto",
        sortDescFirst: false,
        meta: {
          thClassName: "w-80 min-w-[20rem] max-w-[20rem]",
          tdClassName: "w-80 min-w-[20rem] max-w-[20rem] align-top",
        },
        cell: (info) => {
          const part = info.row.original;
          const fmt = (iso: string | null | undefined) =>
            iso ? DateTime.fromISO(iso).toFormat("dd LLL yyyy") : null;

          const events: {
            key: string;
            label: string;
            date: string | null;
            by: string | null;
          }[] = [
            {
              key: "dismantled",
              label: "Dismantled",
              date: fmt(part.removedOn),
              by: part.removedBy,
            },
            {
              key: "imaged",
              label: "Imaged",
              date: fmt(part.imagedOn),
              by: part.imagedBy,
            },
            {
              key: "qc",
              label: "QC",
              date: fmt(part.passedQcOn),
              by: part.passedQcBy,
            },
          ].filter((e) => e.date || e.by);

          const titleClass = editableMpn ? "font-medium text-black" : "font-medium text-slate-800";
          const mutedClass = editableMpn ? "text-xs text-black" : "text-xs text-slate-500";
          const subtleClass = editableMpn ? "text-black" : "text-slate-400";

          return (
            <div className="flex flex-col gap-0.5">
              <div className="flex items-center gap-2">
                <span className={titleClass}>
                  {part.category?.name ?? "—"}
                </span>
                <PartGradeBadge part={part} />
                <PartNotesIndicator part={part} />
              </div>
              {part.tagNumber && (
                <div className={mutedClass}>
                  <CopyText text={part.tagNumber} />
                </div>
              )}
              {events.map((event) => (
                <div
                  key={event.key}
                  className={classNames("flex flex-wrap items-center gap-x-2", mutedClass)}
                >
                  <span>
                    {event.label}
                    {event.date ? ` ${event.date}` : ""}
                  </span>
                  {event.by && (
                    <span className={subtleClass}>
                      by <ContactFromId contactId={event.by} />
                    </span>
                  )}
                </div>
              ))}
            </div>
          );
        },
      }),
      colHelper.accessor("vehicle.combinedMake", {
        header: "Donor Vehicle",
        enableSorting: false,
        cell: (info) => (
          <DonorVehicleCell part={info.row.original} twoColumn={editableMpn} />
        ),
      }),
      colHelper.accessor("partNumber", {
        header: "MPN",
        sortingFn: "auto",
        meta: editableMpn
          ? {
              thClassName: "w-72 min-w-[18rem] max-w-[18rem]",
              tdClassName: "w-72 min-w-[18rem] max-w-[18rem] align-top",
            }
          : undefined,
        cell: (info) => {
          if (editableMpn) return <StockMpnCell part={info.row.original} />;
          const value = info.getValue();
          return value ? (
            <CopyText text={value} />
          ) : (
            <span className="text-xs text-slate-400">—</span>
          );
        },
      }),
      colHelper.accessor(
        (row) => {
          const offer = row.offers?.[0];
          const offerPrice = offer
            ? offer.format === FormatType.Auction
              ? offer.auctionStartPrice
              : offer.buyItNowPrice
            : null;
          return offerPrice ?? row.enteredPrice ?? 0;
        },
        {
          id: "offer.price",
          header: "Price",
          meta: editableMpn
            ? {
                thClassName: "w-64 min-w-[16rem] max-w-[16rem]",
                tdClassName: "w-64 min-w-[16rem] max-w-[16rem] align-top",
              }
            : undefined,
          sortingFn: (rowA: any, rowB: any, columnId: any) => {
            return (
              parseFloat(rowA.original.offers[0]?.auctionStartPrice ?? 0) +
              parseFloat(rowA.original.offers[0]?.buyItNowPrice ?? 0) -
              parseFloat(rowB.original.offers[0]?.auctionStartPrice ?? 0) -
              parseFloat(rowB.original.offers[0]?.buyItNowPrice ?? 0)
            );
          },
          cell: (info) => {
            if (info.row.original.isCore)
              return <div>£{info.row.original.enteredPrice?.toFixed(2)}</div>;
            return (
              <UnlistedOfferPrice
                part={info.row.original}
                hideLabel
                intelligencePopover={!editableMpn}
                inlineIntelligence={editableMpn}
                showButtons={false}
                compact
              />
            );
          },
        }
      ),
      colHelper.accessor("warehouseLocationId", {
        header: "Location",
        sortingFn: "auto",
        sortDescFirst: false,
        meta: {
          thClassName: "w-48 min-w-[12rem] max-w-[12rem]",
          tdClassName: "w-48 min-w-[12rem] max-w-[12rem] align-top",
        },
        cell: (info) => (
          <WarehouseLocationFromId
            id={info.getValue()}
            locatedById={info.row.original.locatedBy}
            lastStockChecked={info.row.original.lastStockChecked}
          />
        ),
      }),
      colHelper.display({
        id: "status",
        header: "Status",
        meta: editableMpn
          ? {
              thClassName: "w-40 min-w-[10rem] max-w-[10rem]",
              tdClassName: "w-40 min-w-[10rem] max-w-[10rem] align-top",
            }
          : undefined,
        cell: (info) => <StatusIcons part={info.row.original} />,
      }),
      ...(showQcReason
        ? [
            colHelper.accessor("qcReason", {
              id: "qcReason",
              header: "QC Reason",
              enableSorting: false,
              meta: {
                thClassName: "w-64 min-w-[16rem] max-w-[20rem]",
                tdClassName: "w-64 min-w-[16rem] max-w-[20rem] align-top",
              },
              cell: (info) => {
                const reason = info.getValue();
                if (!reason) {
                  return <span className="text-xs text-slate-400">—</span>;
                }
                return (
                  <p className="whitespace-pre-line text-xs leading-snug text-slate-700 line-clamp-4">
                    {reason}
                  </p>
                );
              },
            }),
          ]
        : []),
    ],
    [editableMpn, showQcReason]
  );

  const handleSendToQc = (rows: RowModel<IPart>) => {
    setPartsToQc(rows.rows.map((row) => row.original));
    setSendToQcOpen(true);
  };

  const handleMultiPrint = async (rows: RowModel<IPart>) => {
    var parts = new Array();

    rows.rows.forEach((row) => {
      parts.push(row.original);
    });

    let htmlPages = [] as string[];

    // Use Promise.all to wait for all promises to resolve
    await Promise.all(parts.map(async (part) => {
      const html = await generatePartLabel(part);
      htmlPages.push(html);
    }));

    //Stitch together with a page break inbetween each
    const allHtml = htmlPages.join("<div style='page-break-after: always;'></div>");

    var iframe = document?.getElementById("frame") as HTMLIFrameElement;
    var pri = iframe.contentWindow;
    pri?.document.open();
    pri?.document.write(allHtml);
    pri?.document.close();
    pri?.focus();

    //QR code doesn't show up without a timeout
    setTimeout(async () => {
      pri?.print();
    }, 1);

    // table.resetRowSelection();
  };


  const handleDeleteParts = async () => {
    if (!toDelete) return;

    setDeleteLoading(true);

    try {
      await Promise.all(
        toDelete.rows.map(async (row) => {
          await deletePart.mutateAsync(row.original.id);

          // Deselect the row after deletion
          row.toggleSelected(false);
        })
      );

      deleteHandlers.onSuccess();
      setToDelete(undefined);
      setConfirmOpen(false);
      queryClient.invalidateQueries(["pagedTable", pageMode]);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleGetVehicle = () => {
    const vrm = watch("vrm");
    if (!vrm || fetchLoading || isSearching || isSearchDisabled) return;

    // Clear any existing vehicle timeout
    if (vehicleTimeoutRef.current) {
      clearTimeout(vehicleTimeoutRef.current);
    }

    setFetchLoading(true);
    vrmLookup.mutate(vrm, {
      onSuccess: (data) => {

        const vehicle = data.data;
        if (vehicle) {
          setValue("make", (vehicle.combinedMake ?? "").trim() as any);
          setValue("model", (vehicle.modelRangeDescription ?? "").trim() as any);
          setValue("year", vehicle.dvlaYearOfManufacture?.toString() ?? "");

          setValue("bodyStyle", vehicle.bodyStyle);
          setValue("numberOfDoors", vehicle.numberOfDoors?.toString() ?? "");
          setValue("fuelType", vehicle.combinedFuelType);
          setValue("transmissionType", vehicle.combinedTransmission);
          setValue("engineCode", vehicle.engineModelCode);


          var yearFrom = vehicle.visibilityDate && DateTime.fromISO(vehicle.visibilityDate).year;
          var yearTo = vehicle.terminateDate && DateTime.fromISO(vehicle.terminateDate).year;
          setValue("yearFrom", yearFrom?.toString() ?? "");
          setValue("yearTo", yearTo?.toString() ?? "");

          const engineSize = vehicle.engineCapacity
            ? (Math.round(vehicle.engineCapacity / 100) * 100 / 1000).toString()
            : "";
          setValue("engineSize", engineSize);
        }
      },
      onError: () => {
        // addNotification({

        // })
      },
      onSettled: () => {
        // Add a small delay before allowing another vehicle lookup
        vehicleTimeoutRef.current = setTimeout(() => {
          setFetchLoading(false);
        }, 500);
      },
    });
  };

  const handleSmartSearch = () => {
    const data = getValues();

    if (!data.vrm || isSearching || fetchLoading) return;

    setIsSearching(true);
    setIsSmartMatch(true);
    setAdditionalBody({
      smartSearch: data.vrm,
      categoryId: data.partCategory?.id,
      listingStatus: data.listingStatus,
    });

    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Reset search flag after a delay to allow the query to start
    timeoutRef.current = setTimeout(() => setIsSearching(false), 1000);
  };

  const handleClearForm = () => {
    reset();
    setIsSmartMatch(false);
    setDates({ startDate: null, endDate: null });
    setDateFieldType("Created");
    setAdditionalBody({});
    setQueryInput("");
    setAppliedQuery("");
  };

  const handleStatusUpdate = () => {
    const body = {
      partIds: toUpdate?.rows.map((row) => row.original.id),
      data: {
        partStatus: statusUpdate,
      }
    };

    bulkPatchPart.mutate(body, {
      ...saveHandlers,
      onSuccess: () => {
        saveHandlers.onSuccess();
        setToUpdate(undefined);
        setUpdateStatusOpen(false);
        setStatusUpdate(0);
      },
    });
  };

  const handlePriceUpdate = () => {
    const body = {
      offers: toUpdate?.rows.map((row) => ({
        offerId: row.original.offers[0]?.id,
        partId: row.original.id,
      })),
      price: priceUpdate,
    };

    bulkUpdateOfferPrice.mutate(body, {
      ...saveHandlers,
      onSuccess: () => {
        saveHandlers.onSuccess();
        setToUpdate(undefined);
        setUpdatePriceOpen(false);
        setPriceUpdate(0);
      },
    });
  };

  const handleAddToBasket = (rows: RowModel<IPart>) => {
    let partIds = rows.rows.map((row) => row.original.id);

    if (!partIds || partIds.length === 0) return;

    addItemsMultiple.mutate(partIds);
  };

  const handleAddToWorkOrder = (rows: RowModel<IPart>) => {
    const items: SearchPaletteItem[] = rows.rows
      .map((row) => row.original)
      .filter((part) => Boolean(part.id))
      .map((part) => ({
        id: part.id!,
        name: `${part.tagNumber ?? "Unknown Tag"} - ${part.title ?? "Part"}`,
        type: "Stock",
      }));

    setWorkOrderItems(items);
    setIsBulkWorkOrderOpen(true);
  };

  //Search button
  const onSubmit = (data: IFormFields) => {
    if (isSearching || fetchLoading) return; // Prevent multiple simultaneous searches

    // Prevent form submission if the component is not fully ready
    if (!data || typeof data !== 'object') return;

    setIsSearching(true);

    let dataCopy: Partial<IFormFields> = {};

    if (!isExtended) {
      //Don't send values we can't see
      dataCopy = {
        ...defaultFormValues,
        make: data.make,
        model: data.model,
        trim: data.trim,
        year: data.year,
        yearFrom: data.yearFrom,
        yearTo: data.yearTo,
        tagNumber: data.tagNumber,
        partNumber: data.partNumber,
        partCategory: data.partCategory,
        listingStatus: data.listingStatus,
        locationSearchMode: data.locationSearchMode,
        warehouseLocationId: data.locationSearchMode === "equals" ? data.warehouseLocation?.id : undefined,
        warehouseLocationBeginsWith: data.locationSearchMode === "beginsWith" ? data.warehouseLocationBeginsWith : undefined,
      };
    } else {
      dataCopy = { ...data };
      dataCopy.warehouseLocationId = data.locationSearchMode === "equals" ? data.warehouseLocation?.id : undefined;
      dataCopy.warehouseLocationBeginsWith = data.locationSearchMode === "beginsWith" ? data.warehouseLocationBeginsWith : undefined;
    }

    dataCopy.vrm = undefined;
    dataCopy.smartSearch = undefined;
    setIsSmartMatch(false);

    const triFlags = buildTriStateFlags({
      hasWorkOrder: dataCopy.hasWorkOrder,
      hasImages: dataCopy.hasImages,
      hasMPN: dataCopy.hasMPN,
      hasLocation: dataCopy.hasLocation,
    });

    // Strip the raw "any | with | without" strings and the raw partStatus value
    // — they're already translated into the boolean flags / conditional value
    // below, and sending them alongside confuses the search endpoint.
    const {
      hasWorkOrder: _hwo,
      hasImages: _hi,
      hasMPN: _hm,
      hasLocation: _hl,
      partStatus: rawPartStatus,
      ...cleanData
    } = dataCopy as IFormFields;

    // Add date range filters
    const bodyWithDates = {
      ...cleanData,
      startDate: dates.startDate?.toISOString(),
      endDate: dates.endDate?.toISOString(),
      dateFieldType: dateFieldType,
      categoryId: dataCopy.partCategory?.id,
      ...triFlags,
      partStatus:
        typeof rawPartStatus === "number" && rawPartStatus >= 0
          ? rawPartStatus
          : undefined,
    };

    setAdditionalBody(bodyWithDates);
    setAppliedQuery(queryInput);

    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Reset search flag after a delay to allow the query to start
    timeoutRef.current = setTimeout(() => setIsSearching(false), 1000);
  };

  const handleDatesChange = (newDates: DateRangeType) => {
    setDates(newDates);
  };

  const handleMakeSelect = (make: { make?: string } | null) => {
    setValue("make", make?.make ?? "" as any);
    setValue("model", "" as any);
    setValue("trim", "" as any);
  };

  // Monitor PagedTable query state to prevent crashes
  const [queryState, setQueryState] = useState<any>(null);

  useEffect(() => {
    // Subscribe to query cache changes and find any PagedTable queries
    const unsubscribe = queryClient.getQueryCache().subscribe(() => {
      const cache = queryClient.getQueryCache();
      const pagedTableQueries = cache.getAll().filter(query =>
        query.queryKey[0] === "pagedTable"
      );

      // Get the most recent PagedTable query state
      const latestQuery = pagedTableQueries[pagedTableQueries.length - 1];
      setQueryState(latestQuery?.state || null);
    });
    return () => unsubscribe();
  }, [queryClient]);

  const isSearchDisabled = useMemo(
    () => !queryState || queryState.status === "loading" || isSearching || fetchLoading,
    [queryState, isSearching, fetchLoading]
  );

  // Cleanup effect to prevent memory leaks
  useEffect(() => {
    return () => {
      // Cleanup any pending timeouts
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (vehicleTimeoutRef.current) {
        clearTimeout(vehicleTimeoutRef.current);
      }
    };
  }, []);

  const handleOpenChangeCategory = (rows: RowModel<IPart>) => {
    setToUpdate(rows);
    setUpdateCategoryOpen(true);
  };

  const actionButtons = useMemo(() => [
    { label: "Change Category", onClick: handleOpenChangeCategory, disabledOnNoRows: true },
    { label: "Send to QC", onClick: handleSendToQc, disabledOnNoRows: true },
    { label: "Reprint Labels", onClick: handleMultiPrint, disabledOnNoRows: true },
    {
      label: "Change Part Status",
      disabledOnNoRows: true,
      onClick: (rows: RowModel<IPart>) => {
        setToUpdate(rows);
        setUpdateStatusOpen(true);
      },
    },
    {
      label: "Change Price",
      disabledOnNoRows: true,
      onClick: (rows: RowModel<IPart>) => {
        setToUpdate(rows);
        setUpdatePriceOpen(true);
      },
    },
    {
      label: "Add To Basket",
      disabledOnNoRows: true,
      onClick: handleAddToBasket,
    },
    {
      label: "Add to work order",
      disabledOnNoRows: true,
      onClick: handleAddToWorkOrder,
    },
    {
      label: "Delete Parts",
      disabledOnNoRows: true,
      onClick: (rows: RowModel<IPart>) => {
        setToDelete(rows);
        setConfirmOpen(true);
      },
    },
  ], [handleSendToQc, handleMultiPrint, handleAddToBasket, handleAddToWorkOrder]);

  const watchMake = watch("make");
  const watchModel = watch("model");
  const watchTrim = watch("trim");
  const watchYearFrom = watch("yearFrom");
  const watchYearTo = watch("yearTo");
  const watchBodyStyle = watch("bodyStyle");
  const watchNumberOfDoors = watch("numberOfDoors");
  const watchFuelType = watch("fuelType");
  const watchEngineSize = watch("engineSize");
  const watchEngineCode = watch("engineCode");
  const watchTransmission = watch("transmissionType");
  const watchHasWorkOrder = watch("hasWorkOrder");
  const watchHasImages = watch("hasImages");
  const watchHasMPN = watch("hasMPN");
  const watchHasLocation = watch("hasLocation");

  const yearFromNumeric = watchYearFrom ? Number(watchYearFrom) : undefined;
  const yearToNumeric = watchYearTo ? Number(watchYearTo) : undefined;

  const watchListingStatus = watch("listingStatus");
  const watchPartStatus = watch("partStatus");
  const watchWarehouseLocationId = watch("warehouseLocationId");
  const watchWarehouseLocationBeginsWith = watch("warehouseLocationBeginsWith");

  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (typeof watchListingStatus === "number" && watchListingStatus !== ebayAllValue) count += 1;
    if (typeof watchPartStatus === "number" && watchPartStatus >= 0) count += 1;
    if (watchWarehouseLocationId || watchWarehouseLocationBeginsWith) count += 1;
    if (dates.startDate || dates.endDate) count += 1;
    if (watchHasWorkOrder && watchHasWorkOrder !== "any") count += 1;
    if (watchHasImages && watchHasImages !== "any") count += 1;
    if (watchHasMPN && watchHasMPN !== "any") count += 1;
    if (watchHasLocation && watchHasLocation !== "any") count += 1;
    return count;
  }, [
    watchListingStatus,
    watchPartStatus,
    watchWarehouseLocationId,
    watchWarehouseLocationBeginsWith,
    dates.startDate,
    dates.endDate,
    watchHasWorkOrder,
    watchHasImages,
    watchHasMPN,
    watchHasLocation,
  ]);

  return (
    <>
      <ChangeCategoryModal open={updateCategoryOpen} setOpen={setUpdateCategoryOpen} setToUpdate={setToUpdate} parts={toUpdate?.rows.map((row) => row.original)!} />
      <SendToQcModal
        open={sendToQcOpen}
        setOpen={setSendToQcOpen}
        parts={partsToQc}
        onCompleted={() => {
          saveHandlers.onSuccess();
          queryClient.invalidateQueries(["pagedTable", pageMode]);
        }}
        onFailed={saveHandlers.onError}
      />
      <Modal open={updateStatusOpen} setOpen={setUpdateStatusOpen} width="max-w-3xl">
        <div className="space-y-4">
          <p>You are about to update {toUpdate?.rows.length} parts!</p>
          <div className="w-full grid grid-cols-1 lg:grid-cols-2 gap-2">
            <select value={statusUpdate} className={`border p-1`} onChange={(e) => setStatusUpdate(parseInt(e.target.value))}>
              {Object.values(PartStatus).filter((ct) => isNaN(Number(ct))).map((status, i) => (
                <option key={status} value={i}>
                  {status}
                </option>
              ))}
            </select>
            <ButtonInput label={"Update All"} classes={""} onClick={handleStatusUpdate} />
          </div>
        </div>
      </Modal>
      <Modal open={updatePriceOpen} setOpen={setUpdatePriceOpen} width="max-w-3xl">
        <div className="space-y-4">
          <p>You are about to update {toUpdate?.rows.length} parts!</p>
          <div className="w-full grid grid-cols-1 lg:grid-cols-2 gap-2">
            <div className="flex items-center w-full">
              £ <input
                step={0.01}
                defaultValue={priceUpdate}
                className="h-8 w-full border p-1 text-sm" type="number"
                onChange={(e) => setPriceUpdate(parseFloat(e.target.value))}
              />
            </div>
            <ButtonInput label={"Update All"} classes={""} onClick={handlePriceUpdate} />
          </div>
        </div>
      </Modal>
      <ConfirmModal
        open={confirmOpen}
        setOpen={setConfirmOpen}
        title={"Delete these parts?"}
        message={`Are you sure you want to delete these parts? \n This action cannot be undone.`}
        confirmButtonText={"Delete"}
        onConfirm={handleDeleteParts}
        isLoading={deleteLoading}
      />
      <BulkAddToWorkOrderModal
        open={isBulkWorkOrderOpen}
        onClose={() => {
          setIsBulkWorkOrderOpen(false);
          setWorkOrderItems([]);
        }}
        selectedItems={workOrderItems}
      />

      <form onSubmit={handleSubmit(onSubmit)} className="mt-2 rounded-xl border border-slate-200 bg-white p-3 shadow-sm space-y-2">

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-3 items-end">
          <div className="lg:col-span-3">
          <label className="text-sm font-medium text-black">
                          Get Vehicle Details
                        </label>
            <div
              className={classNames(
                "mt-1 flex items-stretch overflow-hidden rounded-md border-2 border-black shadow-sm",
                "focus-within:ring-2 focus-within:ring-primary-500"
              )}
            >
              <span
                aria-hidden="true"
                className="flex w-6 items-center justify-center bg-blue-700 text-[10px] font-bold uppercase tracking-widest text-yellow-300"
              >
                UK
              </span>
              <input
                {...register("vrm")}
                disabled={fetchLoading || isSearching}
                placeholder="ENTER VRM / VIN"
                aria-label="VRM or VIN"
                className={classNames(
                  fetchLoading || isSearching ? "opacity-60" : "",
                  "w-full border-0 bg-yellow-300 px-3 py-1 text-center text-xl font-bold uppercase tracking-[0.25em] text-black placeholder:text-black/40 placeholder:tracking-[0.15em] placeholder:text-sm focus:outline-none focus:ring-0"
                )}
                style={{
                  fontFamily:
                    '"UKNumberPlate", "Charles Wright", "Arial Black", "Helvetica Neue", sans-serif',
                }}
              />
              <button
                type="button"
                onClick={handleGetVehicle}
                disabled={isSearchDisabled || fetchLoading}
                title={fetchLoading ? "Fetching..." : "Fetch vehicle details"}
                aria-label="Fetch vehicle details"
                className={classNames(
                  "flex w-9 items-center justify-center bg-black text-yellow-300 transition-colors",
                  isSearchDisabled || fetchLoading
                    ? "cursor-not-allowed opacity-60"
                    : "hover:bg-slate-800 cursor-pointer"
                )}
              >
                {fetchLoading ? (
                  <ArrowPathIcon className="h-5 w-5 animate-spin" aria-hidden="true" />
                ) : (
                  <MagnifyingGlassIcon className="h-5 w-5" aria-hidden="true" />
                )}
              </button>
            </div>
          </div>
          <div className="lg:col-span-3">
                <ComboBoxInput
                  label={"Part Type"}
                  register={register}
                  registerName={"partCategory"}
                  options={allCategories.data?.data ?? []}
                  setValue={setValue}
                  disabled={isSearchDisabled || allCategories.isLoading}
                  searchField={"name"}
                  classes="w-full"
                  placeholder="Search categories..."
                  labelClass="text-black"
                />
          </div>
          <div className="lg:col-span-1">
            <label className="text-sm font-medium text-wilma-700">
              Part Number / MPN
            </label>
            <div className="relative mt-1">
              <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-2 text-wilma-500">
                <HashtagIcon className="h-4 w-4" aria-hidden="true" />
              </span>
              <input
                {...register("partNumber")}
                type="text"
                placeholder="MPN..."
                disabled={isSearchDisabled}
                className={classNames(
                  isSearchDisabled ? "bg-gray-100" : "bg-wilma-50",
                  "w-full rounded-md border border-wilma-400 py-2 pl-7 pr-3 text-sm font-medium leading-5 text-wilma-900 placeholder:text-wilma-400 placeholder:font-normal focus:border-wilma-500 focus:outline-none focus:ring-1 focus:ring-wilma-500"
                )}
              />
            </div>
          </div>
          <div className="lg:col-span-2">
            <label className="text-sm font-medium text-wilma-700">
              Tag Number
            </label>
            <div className="relative mt-1">
              <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-2 text-wilma-500">
                <TagIcon className="h-4 w-4" aria-hidden="true" />
              </span>
              <input
                {...register("tagNumber")}
                type="text"
                placeholder="Tag..."
                disabled={isSearchDisabled}
                className={classNames(
                  isSearchDisabled ? "bg-gray-100" : "bg-wilma-50",
                  "w-full rounded-md border border-wilma-400 py-2 pl-7 pr-3 text-sm font-medium leading-5 text-wilma-900 placeholder:text-wilma-400 placeholder:font-normal focus:border-wilma-500 focus:outline-none focus:ring-1 focus:ring-wilma-500"
                )}
              />
            </div>
          </div>
          <div className="lg:col-span-3 flex flex-wrap gap-2 lg:justify-end items-end">
            <ButtonInput
              label={isSearching ? "Searching..." : "Search"}
              isSubmit
              classes={classNames("!h-10 !px-3 !py-0", isSearchDisabled ? "" : "!bg-primary-600 hover:!bg-primary-700")}
              onClick={undefined}
              disabled={isSearchDisabled}
              isLoading={isSearching}
            />
            <ButtonInput
              label="Smart Match"
              onClick={handleSmartSearch}
              classes={classNames("!h-10 !px-3 !py-0", isSearchDisabled || fetchLoading ? "" : "!bg-[#00909c] hover:!bg-[#00b3c2]")}
              disabled={isSearchDisabled || fetchLoading}
              isLoading={isSearching}
            />
            <PopOutBox position="left" classes="z-30 w-96">
              <p>Smart Match will check for parts that fit your vehicle based on key details like make, model, year, and other specifications.</p>
              <p>It finds body parts that match your car's exterior and mechanical parts that fit your engine and transmission.</p>
            </PopOutBox>
            <ButtonInput
              label={"Clear"}
              classes={classNames("!h-10 !px-3 !py-0", isSearchDisabled ? "" : `!bg-white !text-gray-700 !border !border-gray-300 hover:!bg-gray-100`)}
              onClick={handleClearForm}
              disabled={isSearchDisabled}
            />
          </div>
        </div>

        <div
          className={classNames(
            isSmartMatch ? "[&_input]:!bg-gray-200" : "",
            "grid grid-cols-2 gap-x-2 gap-y-1 sm:grid-cols-4 md:grid-cols-6 xl:grid-cols-[repeat(19,minmax(0,1fr))]"
          )}
        >
          <div className="xl:col-span-1">
            <YearCombobox
              label="Year From"
              currentValue={watchYearFrom}
              maxYear={yearToNumeric}
              onYearSelect={(year) => setValue("yearFrom", (year ?? "") as any)}
              placeholder="From..."
              disabled={isSearchDisabled}
            />
          </div>
          <div className="xl:col-span-1">
            <YearCombobox
              label="Year To"
              currentValue={watchYearTo}
              minYear={yearFromNumeric}
              onYearSelect={(year) => setValue("yearTo", (year ?? "") as any)}
              placeholder="To..."
              disabled={isSearchDisabled}
            />
          </div>
          <div className="xl:col-span-2">
            <MakeCombobox label="Make" onMakeSelect={handleMakeSelect} currentValue={watchMake} />
          </div>
          <div className="xl:col-span-2">
            <ModelCombobox
              label="Model"
              make={watchMake}
              onModelSelect={(model) => {
                setValue("model", (model?.range ?? "") as any);
                setValue("trim", "" as any);
              }}
              currentValue={watchModel}
            />
          </div>
          <div className="xl:col-span-2">
            <TrimCombobox
              label="Trim"
              make={watchMake}
              model={watchModel}
              onTrimSelect={(trim) => setValue("trim", (trim ?? "") as any)}
              currentValue={watchTrim}
            />
          </div>
          <div className="xl:col-span-2">
            <FilterStaticCombobox
              label="Body Style"
              value={watchBodyStyle}
              onChange={(v) => setValue("bodyStyle", v as any)}
              options={BODY_STYLE_OPTIONS}
              disabled={isSearchDisabled}
              placeholder="Any"
            />
          </div>
          <div className="xl:col-span-1">
            <FilterStaticCombobox
              label="Doors"
              value={watchNumberOfDoors}
              onChange={(v) => setValue("numberOfDoors", v as any)}
              options={DOORS_OPTIONS}
              disabled={isSearchDisabled}
              placeholder="Any"
            />
          </div>
          <div className="xl:col-span-2">
            <FilterStaticCombobox
              label="Fuel"
              value={watchFuelType}
              onChange={(v) => setValue("fuelType", v as any)}
              options={FUEL_OPTIONS}
              disabled={isSearchDisabled}
              placeholder="Any"
            />
          </div>
          <div className="xl:col-span-2">
            <FilterStaticCombobox
              label="Engine Size"
              value={watchEngineSize}
              onChange={(v) => setValue("engineSize", v as any)}
              options={ENGINE_SIZE_OPTIONS}
              disabled={isSearchDisabled}
              placeholder="Any"
            />
          </div>
          <div className="xl:col-span-2">
            <FilterStaticCombobox
              label="Engine Code"
              value={watchEngineCode}
              onChange={(v) => setValue("engineCode", v as any)}
              options={[]}
              disabled={isSearchDisabled}
              placeholder="Type code..."
            />
          </div>
          <div className="xl:col-span-2">
            <FilterStaticCombobox
              label="Transmission"
              value={watchTransmission}
              onChange={(v) => setValue("transmissionType", v as any)}
              options={TRANSMISSION_OPTIONS}
              disabled={isSearchDisabled}
              placeholder="Any"
            />
          </div>
        </div>

        {isSmartMatch && (
          <div className="bg-blue-50 border border-blue-200 rounded-md p-3 mt-2">
            <p className="text-blue-800 text-sm">
              <strong>Smart Match Active:</strong> Searching for parts that match your vehicle specifications.
              Vehicle details are locked and cannot be modified while smart match is active.
            </p>
          </div>
        )}

        <hr className="mt-1" />

        <div className="rounded-lg border border-slate-200 bg-slate-50/60">
          <button
            type="button"
            onClick={() => setFiltersOpen((v) => !v)}
            aria-expanded={filtersOpen}
            className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left hover:bg-slate-100/70 transition-colors"
          >
            <span className="flex items-center gap-2">
              <AdjustmentsHorizontalIcon className="h-4 w-4 text-slate-500" aria-hidden="true" />
              <span className="text-xs font-semibold uppercase tracking-wide text-slate-600">
                Filters
              </span>
              {activeFiltersCount > 0 && (
                <span className="inline-flex items-center rounded-full bg-wilma-100 px-2 py-0.5 text-[11px] font-semibold text-wilma-700">
                  {activeFiltersCount} active
                </span>
              )}
              {!filtersOpen && activeFiltersCount === 0 && (
                <span className="text-[11px] font-medium text-slate-400">
                  No filters applied
                </span>
              )}
            </span>
            {filtersOpen ? (
              <ChevronUpIcon className="h-4 w-4 text-slate-500" aria-hidden="true" />
            ) : (
              <ChevronDownIcon className="h-4 w-4 text-slate-500" aria-hidden="true" />
            )}
          </button>

          <CollapsibleSection isOpen={filtersOpen}>
            <div className="px-3 pt-1 pb-3">
              <div className="flex flex-wrap items-end gap-x-4 gap-y-2">
                <FilterSegmented<number>
                  label="eBay Status"
                  value={watchListingStatus ?? ebayAllValue}
                  onChange={(v) => setValue("listingStatus", v as any)}
                  segments={ebayListingSegments}
                  disabled={isSearchDisabled}
                />
                <FilterTriToggle
                  label="Work Order"
                  value={watchHasWorkOrder}
                  onChange={(v) => setValue("hasWorkOrder", v as any)}
                  disabled={isSearchDisabled}
                />
                <FilterTriToggle
                  label="Images"
                  value={watchHasImages}
                  onChange={(v) => setValue("hasImages", v as any)}
                  disabled={isSearchDisabled}
                />
                <FilterTriToggle
                  label="MPN"
                  value={watchHasMPN}
                  onChange={(v) => setValue("hasMPN", v as any)}
                  disabled={isSearchDisabled}
                />
                <FilterTriToggle
                  label="Location"
                  value={watchHasLocation}
                  onChange={(v) => setValue("hasLocation", v as any)}
                  disabled={isSearchDisabled}
                />

                <WarehouseLocationSearch
                  showSearchOptions={true}
                  register={register}
                  setValue={setValue}
                  watch={watch}
                  isSearchDisabled={isSearchDisabled}
                  warehouseLocations={warehouseLocations}
                />

                <DateRangeFilter
                  dates={dates}
                  onDatesChange={handleDatesChange}
                  dateFieldType={dateFieldType}
                  onDateFieldTypeChange={setDateFieldType}
                  disabled={isSearchDisabled}
                />

                <div className="w-44">
                  <SelectInput
                    label={"Part Status"}
                    register={register}
                    registerName={"partStatus"}
                    options={partStatusOptions}
                    setValue={setValue}
                    watch={watch}
                    cols="w-full"
                    disabled={isSearchDisabled}
                    rounded
                    labelClass="text-black"
                  />
                </div>
              </div>
            </div>
          </CollapsibleSection>
        </div>

        <hr className="mt-1" />

        <div className="relative flex flex-wrap gap-x-4 gap-y-1 items-center">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <MagnifyingGlassIcon
              className="h-5 w-5 text-gray-400"
              aria-hidden="true"
            />
          </div>
          <input
            type="text"
            value={queryInput}
            disabled={isSearchDisabled}
            onChange={(e) => setQueryInput(e.target.value)}
            className="pl-10 sm:text-sm lg:min-w-[250px] sm:min-w-[unset] max-w-full lg:max-w-[calc(100% - 3rem)] border-1 border-gray-300 rounded-md focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            placeholder={"Search stock by Tag, Part No., Make, Model, Engine Code, Category, or Warehouse"}
            style={{ width: `calc(${"Search stock by Tag, Part No., Make, Model, Engine Code, Category, or Warehouse".length}ch - 2rem)` }}
          />
        </div>


      </form >


      {filterBanner}

      <VehicleSearchHighlightsContext.Provider value={vehicleSearchHighlights}>
       <StockSearchVrmContext.Provider value={(watch("vrm") ?? "").toString()}>
        <PagedTable
          key={tableKey}
          url={tableUrl}

          columns={cols}
          pageMode={pageMode}
          queryLabel={undefined}
          externalQuery={appliedQuery}
          onRowDoubleClick={(x: IPart) => openInNewTab(`stock/${x.id}`)}
          actionButtons={showActions ? actionButtons : []}
          showTotal={true}
          additionalBody={{ ...additionalBody, ...externalFilters }}
          expandedRow={(part) => <PartImagesGallery part={part} />}
        />
       </StockSearchVrmContext.Provider>
      </VehicleSearchHighlightsContext.Provider>

      <iframe id="frame" className="h-0 w-0 absolute"></iframe>
    </>
  );
}
